import { useEffect } from 'react';

// The audio-server URL a local file plays through — the row's identity in
// `player-track-change` events. Decoding it back gives the file path.
const LOCAL_AUDIO_URL_RE = /^https?:\/\/localhost:3069\/audio\?path=([^&]+)/;

export function localPathFromMusicSrc(musicSrc) {
    if (typeof musicSrc !== 'string') return null;
    const m = musicSrc.match(LOCAL_AUDIO_URL_RE);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch { return null; }
}

// Keep a local file-row's play/pause icon in sync with the global bottom player.
//
// Library/Manage rows flip their own `isPlaying` optimistically when the user
// clicks the row's play button, but they never hear about a pause / next / stop
// fired from the bottom player itself — so the row's icon gets stuck showing
// "playing" after the user pauses down there. The player already broadcasts a
// `player-track-change` event on every play/pause (see MusicPlayer.js); the row
// just wasn't listening. `musicSrc` in that event is the local audio-server URL
// (`http://localhost:3069/audio?path=<encoded filePath>`), so we can tell
// whether the event is about the row that's currently playing.
//
// Pass `setCurrentlyPlayingPath` to also FOLLOW the player as it auto-advances
// through a local list context (MusicPlayer's advanceListContext): the playing
// highlight moves to the next row instead of staying stuck on the finished one.
// When a non-local track takes over (auto-radio / cloud), the path clears so
// no row claims to be playing.
export function usePlayerRowSync(currentlyPlayingPath, setIsPlaying, setCurrentlyPlayingPath) {
    useEffect(() => {
        if (!currentlyPlayingPath) return undefined;
        const enc = encodeURIComponent(currentlyPlayingPath);
        const onChange = (e) => {
            const { musicSrc, playing } = e.detail || {};
            const matches = typeof musicSrc === 'string'
                && (musicSrc.includes(enc) || musicSrc.includes(currentlyPlayingPath));
            if (matches) {
                setIsPlaying(!!playing);
                return;
            }
            if (!playing) return;
            // A different track took over the player — this row isn't playing.
            if (setCurrentlyPlayingPath) {
                // If it's another LOCAL file (list auto-advance), move the
                // highlight to it; a cloud track clears the highlight.
                const nextPath = localPathFromMusicSrc(musicSrc);
                setCurrentlyPlayingPath(nextPath);
                setIsPlaying(!!nextPath);
            } else {
                setIsPlaying(false);
            }
        };
        window.addEventListener('player-track-change', onChange);
        return () => window.removeEventListener('player-track-change', onChange);
    }, [currentlyPlayingPath, setIsPlaying, setCurrentlyPlayingPath]);
}
